import React from 'react';
import styled, { css } from 'styled-components';
import { observer } from 'mobx-react';

import { PayByBankLogo } from 'src/assets/pay-by-bank-logo';
import { LockIconAlt } from 'src/assets/lock-icon-alt';
import BoltIconAlt from 'src/assets/bolt-alt';
import { Typography } from 'src/typography';
import { RightArrowIconAlt } from 'src/assets/right-arrow-alt';
import { SignupButton } from 'src/payments/components/pay-sign-up-button/pay-sign-up-button';
import { handleScroll } from 'src/utils/scroll';
import { WhiteLabelLogo } from 'src/payments/components';
import { LogoWrapper } from 'src/payments/components/circle-bank-logo';
import { AlertIcon } from 'src/assets/alert-icon';
import { BankLogo } from 'src/account/bank-account/components/existing-user/existing-user.styles';
import { WhatIsPayByBank } from 'src/payments/components/what-is-pbb';
import { getListSeparator } from 'src/checkout/components/sections/payment/shared/utils';
import { usePayByBankSignup } from 'src/checkout/components/sections/payment/shared/use-pay-by-bank-signup';
import { WellsFargoIcon, ChaseIcon, BankOfAmericaIcon } from '../icons';

// Constants
export const SIGNUP_CONSTANTS = {
  HEADER_TEXT: 'Link your bank to pay instantly:',
  SUBHEADER_TEXT: 'Quickly pay with your bank login.',
  ENCRYPTED_TITLE: 'Encrypted data, end-to-end',
  ENCRYPTED_SUBTITLE: 'All we see is your balance, and you can unlink at any time.',
  CONNECT_TITLE: 'Connect your bank in seconds',
  CONNECT_SUBTITLE: 'Simply login with your banking credentials',
  BUTTON_TEXT: 'Link your bank',
  CHANGE_ORDER_TYPE_TEXT: 'change your order type',
  BANK_ICONS: [
    { src: BankOfAmericaIcon.src, alt: 'Bank of America logo', width: '14px', height: '8px' },
    { src: ChaseIcon.src, alt: 'Chase logo', width: '14px', height: '14px' },
    { src: WellsFargoIcon.src, alt: 'Wells Fargo logo', width: '14px', height: '14px' },
  ],
};

export const PayByBankSignup = observer(
  (): JSX.Element => {
    const {
      pbbSignupEnabledForOrderType,
      pbbOnlyEnabledForOtherOrderTypes,
      whiteLabelConfig,
      otherOrderTypesWithPBBEnabled,
      handleClick,
    } = usePayByBankSignup();

    const {
      HEADER_TEXT,
      SUBHEADER_TEXT,
      ENCRYPTED_TITLE,
      ENCRYPTED_SUBTITLE,
      CONNECT_TITLE,
      CONNECT_SUBTITLE,
      BANK_ICONS,
      BUTTON_TEXT,
      CHANGE_ORDER_TYPE_TEXT,
    } = SIGNUP_CONSTANTS;

    if (!pbbSignupEnabledForOrderType && !pbbOnlyEnabledForOtherOrderTypes) {
      return <></>;
    }

    return (
      <>
        <Header size='large' weight='semi'>
          {HEADER_TEXT}
        </Header>
        <Container>
          {pbbOnlyEnabledForOtherOrderTypes && (
            <BlurOverlay>
              <BlurBackground />
              <BlurContent>
                <AlertIcon />
                <Text size='medium'>'Pay now' is only available for</Text>
                <DeliveryTypes>
                  {otherOrderTypesWithPBBEnabled.map((orderType, index) => {
                    const separator = getListSeparator(index, otherOrderTypesWithPBBEnabled.length);
                    return (
                      <Text size='medium' key={orderType}>
                        <strong>{orderType}</strong>
                        {separator}
                      </Text>
                    );
                  })}
                </DeliveryTypes>
                <Text size='medium'>
                  at this location,{' '}
                  <HyperLink href='#orderType' onClick={handleScroll}>
                    {CHANGE_ORDER_TYPE_TEXT}
                  </HyperLink>{' '}
                  or select 'Pay in-store' to proceed.
                </Text>
              </BlurContent>
            </BlurOverlay>
          )}
          <Body>
            <Title>
              {whiteLabelConfig.useWhiteLabel ? (
                <WhiteLabelLogo variant='dark' height='18' />
              ) : (
                <PayByBankLogo width='93.664' height='18' />
              )}
              <Subtitle>{SUBHEADER_TEXT}</Subtitle>
            </Title>
            <InfoContainer>
              <SignupButton
                overrides={buttonOverrides}
                data-testid='dutchie-pay-signup-button'
                onClick={handleClick}
                disabled={pbbOnlyEnabledForOtherOrderTypes}
                aria-label='dutchiePay'
              >
                <BankIcons>
                  {BANK_ICONS.map((icon, index) => (
                    <LogoWrapper diameter={20} key={icon.src} margin='0 0 0 -6px' zIndex={BANK_ICONS.length - index}>
                      <BankLogo alt={icon.alt} width={icon.width} height={icon.height} src={icon.src} />
                    </LogoWrapper>
                  ))}
                  <LogoWrapper diameter={20} margin='0 0 0 -6px'>
                    <PlusIcon>+</PlusIcon>
                  </LogoWrapper>
                </BankIcons>
                <TextIconContainer>
                  <span>{BUTTON_TEXT}</span>
                  <RightArrowIconAlt />
                </TextIconContainer>
              </SignupButton>
              <InfoDetailContainer>
                <LockIconAlt />
                <DetailsContent>
                  <DetailsTitle weight='semi' size='medium'>
                    {ENCRYPTED_TITLE}
                  </DetailsTitle>
                  <DetailsSubtitle size='xsmall'>{ENCRYPTED_SUBTITLE}</DetailsSubtitle>
                </DetailsContent>
              </InfoDetailContainer>
              <InfoDetailContainer>
                <BoltIconAlt />
                <DetailsContent>
                  <DetailsTitle weight='semi' size='medium'>
                    {CONNECT_TITLE}
                  </DetailsTitle>
                  <DetailsSubtitle size='xsmall'>{CONNECT_SUBTITLE}</DetailsSubtitle>
                </DetailsContent>
              </InfoDetailContainer>
            </InfoContainer>
          </Body>
          <Footer>
            <WhatIsPayByBank />
          </Footer>
        </Container>
      </>
    );
  }
);

const Header = styled(Typography.Body)`
  align-self: stretch;
  color: ${({ theme: { colors } }) => colors.primaryBlack};
  line-height: 20px;
`;

const Container = styled.div`
  ${({ theme: { spaces, colors } }) => css`
    display: flex;
    position: relative;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    align-self: stretch;
    border-radius: ${spaces[2]};
    border: 1px solid #dee3e5;
    background: ${colors.white};
    width: 100%;
    height: 100%;
  `}
`;

const BaseBlurOverlay = css`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: ${({ theme: { spaces } }) => spaces[2]};
`;

const BlurOverlay = styled.div`
  ${BaseBlurOverlay}
  display: flex;
  justify-content: center;
  align-items: center;
  backdrop-filter: blur(2px);
  z-index: 10;
`;

const BlurBackground = styled.div`
  ${BaseBlurOverlay}
  background: #0b2034;
  backdrop-filter: blur(20px);
  opacity: 0.5;
  z-index: 9;
`;

const BlurContent = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  flex-direction: column;
  padding: ${({ theme: { spaces } }) => spaces[8]};
  justify-content: center;
  align-items: center;
  gap: 10px;
  z-index: 11;
`;

const Body = styled.div`
  ${({ theme: { spaces } }) => css`
    display: flex;
    max-width: 320px;
    padding: ${spaces[6]} ${spaces[4]};
    flex-direction: column;
    align-items: center;
    gap: ${spaces[6]};
    align-self: center;
  `}
`;

const Title = styled.div`
  display: flex;
  max-width: 262px;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme: { spaces } }) => spaces[2]};
  align-self: stretch;
`;

const Subtitle = styled.p`
  color: #747677;
  text-align: center;
  font: 400 16px/20px Matter;
`;

const Footer = styled.div`
  display: flex;
  padding: 0px 16px 8px 16px;
  justify-content: space-between;
  align-items: center;
  align-self: center;
`;

const InfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme: { spaces } }) => spaces[4]};
  align-self: stretch;
`;

const InfoDetailContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme: { spaces } }) => spaces[2]};
  align-self: stretch;
`;

const DetailsContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: ${({ theme: { spaces } }) => spaces[1]};
  flex: 1 0 0;
`;

const DetailsTitle = styled(Typography.Body)`
  color: #5d666d;
  line-height: 20px;
`;

const DetailsSubtitle = styled(Typography.Body)`
  color: #747677;
  line-height: 16px;
`;

const buttonOverrides = css`
  font: 700 16px / 24px Matter;
  gap: ${({ theme: { spaces } }) => spaces[4]};
`;

const BankIcons = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;
const PlusIcon = styled.p`
  color: #747677;
  text-align: right;
  font: 400 16px / 24px Matter;
`;

const TextIconContainer = styled.div`
  display: flex;
  align-items: center;
`;

const Text = styled(Typography.Body)`
  color: ${({ theme: { colors } }) => colors.white};
  text-align: center;
  line-height: 20px;
  & strong {
    font-weight: 700;
  }
`;

const HyperLink = styled.a`
  ${Text}
  text-decoration: underline;
  text-decoration-line: underline;
  cursor: pointer;
`;

const DeliveryTypes = styled.p`
  text-align: center;
`;
